import { TextLink } from '@edsn/pie-components';
import spinnerIcon from '@iconify/icons-material-symbols/progress-activity';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { useCurrentOrganisation } from '#pie/auth';
import { Card } from '#pie/components/card/Card';
import { ErrorMessageBlock } from '#pie/components/error-message/ErrorMessageBlock';
import { Icon } from '#pie/components/icon/Icon';
import { Page } from '#pie/components/page/Page';
import { Stack } from '#pie/components/stack/Stack';
import { Table } from '#pie/components/table/Table';
import { Text } from '#pie/components/text/Text';
import type { MarketRoleType, ProductKind } from '#pie/internal/api/pie-bff';
import { useGetMarketpartyRegistryOrganizationPreferences } from '#pie/internal/api/pie-bff';

interface PreferredPartyGroupedProducts {
  accountId?: string;
  marketParticipantEanCode?: string | null;
  marketRole?: MarketRoleType;
  product?: ProductKind[];
  statutoryName?: string | null;
  subjectDescription?: string | null;
  subjectId?: string | null;
}

function groupItemsByParticipantWithProducts<
  T extends { marketParticipantEanCode?: string | null; product?: ProductKind },
>(items: T[]): (T & { product: ProductKind[] })[] {
  return items.reduce<(T & { product: ProductKind[] })[]>((grouped, item) => {
    const existingItem = grouped.find(
      (p) => p.marketParticipantEanCode === item.marketParticipantEanCode,
    );

    if (existingItem) {
      /* c8 ignore next */
      existingItem.product = existingItem.product || [];
      if (item.product) {
        /* c8 ignore next */
        const products = Array.isArray(item.product)
          ? item.product
          : [item.product];
        existingItem.product.push(...products);
      }
    } else {
      grouped.push({
        ...item,
        /* c8 ignore next */
        product: item.product
          ? Array.isArray(item.product)
            ? item.product
            : [item.product]
          : [],
      });
    }
    return grouped;
  }, []);
}

export const PreferredPartyAdministration = () => {
  const { t } = useTranslation();
  const { t: thl } = useTranslation('hyperlinks');
  const { data, isLoading, error } =
    useGetMarketpartyRegistryOrganizationPreferences();
  const columnHelper = useMemo(
    () => createColumnHelper<PreferredPartyGroupedProducts>(),
    [],
  );
  const preferredByParties = data?.preferredByParties ?? [];
  const ownPreferences = data?.ownPreferences ?? [];
  const { currentAccount } = useCurrentOrganisation();
  const preferredByPartiesGroupedProducts =
    groupItemsByParticipantWithProducts(preferredByParties);
  const ownPreferenceGroupedProducts =
    groupItemsByParticipantWithProducts(ownPreferences);

  const notFoundError = error as { errorType: string };
  return (
    <Page>
      <Stack gap="md" className="mx-auto mb-6">
        <Stack gap="sm" className="max-w-7xl">
          <Text as="h2" variant="h6">
            {t('preferred_parties_for_me.subtitle')}
          </Text>
          <Text variant="base">
            {t('preferred_parties_for_me.description', {
              marketParty: currentAccount?.name,
            })}
          </Text>
        </Stack>
        <Card className="flex-1 overflow-x-hidden p-4">
          {error != null &&
          (notFoundError == null || notFoundError.errorType !== 'NotFound') ? (
            <ErrorMessageBlock error={error} />
          ) : isLoading ? (
            <Icon
              role="progressbar"
              aria-label="loading"
              icon={spinnerIcon}
              size={48}
              className="m-auto animate-spin text-content-tertiary opacity-50"
            />
          ) : !preferredByPartiesGroupedProducts.length ? (
            <Text className="p-3">{t('common.no_results')}</Text>
          ) : (
            <Table
              columns={[
                columnHelper.accessor('statutoryName', {
                  header: t('common.market_role'),
                  meta: { columnWidth: 250 },
                }),
                columnHelper.accessor('marketParticipantEanCode', {
                  header: t('common.ean'),
                  meta: { columnWidth: 250 },
                }),
                columnHelper.accessor('product', {
                  cell: (info) => {
                    const productCodes = info.getValue() as ProductKind[];
                    return productCodes
                      .map((code) => t(`common.product${code}`))
                      .join(', ');
                  },
                  header: t('common.product'),
                  meta: { columnWidth: 250 },
                }),
                columnHelper.accessor('subjectDescription', {
                  cell: (info) => {
                    return (
                      <ContactGroupLink contactgroup={info.row.original} />
                    );
                  },
                  header: t('common.contact_group'),
                  meta: { columnWidth: 250 },
                }),
              ]}
              sortDescFirst
              data={preferredByPartiesGroupedProducts}
              pagination={{ pageIndex: 0, pageSize: 100 }}
              sorting={[]}
              isLoading={isLoading}
            />
          )}
        </Card>
        <Stack gap="sm" className="max-w-7xl">
          <Text as="h2" variant="h6">
            {t('preferred_parties_from_me.subtitle')}
          </Text>
          <Text variant="base">
            {t('preferred_parties_from_me.description')}
          </Text>
        </Stack>
        <Card className="flex-1 overflow-x-hidden p-4">
          {error != null &&
          (notFoundError == null || notFoundError.errorType !== 'NotFound') ? (
            <ErrorMessageBlock error={error} />
          ) : isLoading ? (
            <Icon
              role="progressbar"
              aria-label="loading"
              icon={spinnerIcon}
              size={48}
              className="m-auto animate-spin text-content-tertiary opacity-50"
            />
          ) : !ownPreferenceGroupedProducts.length ? (
            <Text className="p-3">{t('common.no_results')}</Text>
          ) : (
            <Table
              columns={[
                columnHelper.accessor('statutoryName', {
                  header: t('common.market_role'),
                  meta: { columnWidth: 250 },
                }),
                columnHelper.accessor('marketParticipantEanCode', {
                  header: t('common.ean'),
                  meta: { columnWidth: 250 },
                }),
                columnHelper.accessor('product', {
                  cell: (info) => {
                    const productCodes = info.getValue() as ProductKind[];
                    return productCodes
                      .map((code) => t(`common.product${code}`))
                      .join(', ');
                  },
                  header: t('common.product'),
                  meta: { columnWidth: 250 },
                }),
              ]}
              sortDescFirst
              data={ownPreferenceGroupedProducts}
              pagination={{ pageIndex: 0, pageSize: 100 }}
              sorting={[]}
              isLoading={isLoading}
            />
          )}
        </Card>
        <Text variant="base">
          {t('preferred_parties_more_information')}{' '}
          <TextLink
            href={thl('preferred_party_administration')}
            target="_blank"
            rel="noopener noreferrer"
          >
            {t('site_name')}
          </TextLink>
        </Text>
      </Stack>
    </Page>
  );
};

const ContactGroupLink = ({
  contactgroup,
}: {
  contactgroup: PreferredPartyGroupedProducts;
}) => {
  const subjectDescription = contactgroup.subjectDescription;

  return (
    <Link
      className={'text-action-primary-enabled link'}
      to={`/contactpersonenregister/contactgroep/${contactgroup.subjectId}?filters=${btoa(
        JSON.stringify({ marketPartyIds: [contactgroup.accountId] }),
      )}`}
      aria-label={subjectDescription || ''}
    >
      {contactgroup.subjectDescription}
    </Link>
  );
};
